import { formatUnits, parseUnits } from "viem";
import { STABLE_DECIMALS, PAYOUT_DECIMALS, STABLE_TOKEN, PAYOUT_TOKEN } from "../config";
import { CONTRACT_ERRORS, BPS_DENOMINATOR, WINNERS_BPS } from "./constants";

/** Joins truthy class names. */
export const cx = (...parts) => parts?.filter(Boolean)?.join(" ");

/** Implied chance (%) for each outcome, from its share of the total pool. */
export function outcomeChances(pools) {
    const total = pools?.reduce((sum, p) => sum + p, 0n);
    if (total === 0n || pools?.length === 0) {
        return pools?.map(() => Math.round(1000 / (pools?.length || 1)) / 10);
    }

    const rounded = pools?.map((p) => Math.round((Number((p * 10000n) / total) / 100) * 10) / 10);
    // Rounding can pull the sum off 100 — fold the drift into the largest share.
    const drift = 100 - rounded?.reduce((s, v) => s + v, 0);
    if (Math.abs(drift) >= 0.1) {
        const maxIdx = rounded?.indexOf(Math.max(...rounded));
        rounded[maxIdx] = Math.round((rounded?.[maxIdx] + drift) * 10) / 10;
    }
    return rounded;
}

/** Index of the outcome with the largest pool (0 when all are empty). */
export function leadingOutcome(pools) {
    const chances = outcomeChances(pools);
    return chances?.length ? chances?.indexOf(Math.max(...chances)) : 0;
}

/** How far a market is from an even split — lower means a closer race. */
export function raceSpread(market) {
    const even = 100 / (market?.outcomes?.length || 1);
    return Math.max(0, ...outcomeChances(market?.outcomePools)?.map((c) => Math.abs(c - even)));
}

/** Estimated total return (stake + winnings) if `outcomeIndex` wins — in pool units. */
export function projectPayout(amount, pools, outcomeIndex, winnersBps = BigInt(WINNERS_BPS)) {
    if (amount === 0n) return 0n;
    const winningPool = (pools?.[outcomeIndex] ?? 0n) + amount;
    const totalPool = pools?.reduce((sum, p) => sum + p, 0n) + amount;
    // Winners share their cut of the pool (80%: the rest is the 10% fee and the 10% bonus pool), before any referral deduction
    const distributable = (totalPool * winnersBps) / BigInt(BPS_DENOMINATOR);
    return (amount * distributable) / winningPool;
}

/** "3d 4h 12m" style time left until a unix-seconds timestamp. */
export function timeLeft(endSeconds, nowMs = Date.now()) {
    let secs = Math.floor(endSeconds - nowMs / 1000);
    if (secs <= 0) return "Closed";

    const days = Math.floor(secs / 86400);
    secs -= days * 86400;
    const hours = Math.floor(secs / 3600);
    secs -= hours * 3600;
    const minutes = Math.floor(secs / 60);
    const seconds = secs - minutes * 60;

    const parts = [];
    if (days) parts?.push(`${days}d`);
    if (hours) parts?.push(`${hours}h`);
    if (minutes) parts?.push(`${minutes}m`);
    if (seconds && !days) parts?.push(`${seconds}s`);
    return parts?.join(" ") || "< 1s";
}

/** Compact token amount. Defaults to the 6-decimal pool unit used for stakes and pools. */
export function formatAmount(amount, decimals = STABLE_DECIMALS) {
    const num = parseFloat(formatUnits(amount ?? 0n, decimals));
    if (num === 0) return "0";
    if (num < 0.001) return "< 0.001";
    if (num < 1) return num?.toFixed(4);
    if (num < 1000) return num?.toFixed(2);
    if (num < 1_000_000) return `${(num / 1000)?.toFixed(1)}K`;
    return `${(num / 1_000_000)?.toFixed(1)}M`;
}

/** Exact decimal string (no K/M compaction) — for pre-filling inputs. */
export const exactAmount = (amount, decimals = STABLE_DECIMALS) => formatUnits(amount ?? 0n, decimals);

/** User-typed decimal string → bigint at `decimals`; 0n when empty/invalid. */
export function parseAmount(input, decimals = STABLE_DECIMALS) {
    try {
        const val = parseFloat(input);
        if (!input || isNaN(val) || val <= 0) return 0n;
        return parseUnits(input, decimals);
    } catch {
        return 0n;
    }
}

/** Rescale a raw amount between token decimals (truncating when scaling down). */
export function rescale(amount, from, to) {
    if (from === to) return amount;
    return from < to ? amount * 10n ** BigInt(to - from) : amount / 10n ** BigInt(from - to);
}

export function formatUsd(usd) {
    if (usd === 0) return "$0";
    if (usd < 0.01) return "< $0.01";
    if (usd < 1000) return `$${usd?.toFixed(2)}`;
    if (usd < 1_000_000) return `$${(usd / 1000)?.toFixed(1)}K`;
    return `$${(usd / 1_000_000)?.toFixed(1)}M`;
}

/**
 * Units of a pool/stake amount. The upgraded contract keeps pools and stakes in 18 decimals, polls from before
 * it in 6. A real 18-decimal amount is at least 1e18 (min stake is $1), so anything >= 1e15 raw can only be 18.
 */
export const unitsOf = (...raws) => (raws?.some((r) => (r ?? 0n) >= 10n ** 15n) ? PAYOUT_DECIMALS : STABLE_DECIMALS);

/** Decimals of a known stake token (the app's two tokens), or undefined for anything else. */
export const knownDecimals = (token) => {
    const t = token?.toLowerCase();
    if (t && t === STABLE_TOKEN?.toLowerCase()) return STABLE_DECIMALS;
    if (t && t === PAYOUT_TOKEN?.toLowerCase()) return PAYOUT_DECIMALS;
    return undefined;
};

/** Units a stake is kept in: the poll token's decimals, or detected from the amount for polls from before the upgrade. */
export const stakeUnits = (market, amount) => (market?.token ? knownDecimals(market?.token) ?? unitsOf(amount) : unitsOf(amount));

/**
 * Units a market's pools are kept in. A poll with a token uses that token's native decimals; polls from before the
 * upgrade have none, so their units are detected from the amounts (empty market: 18).
 */
export const marketUnits = (market) => {
    const known = knownDecimals(market?.token);
    if (known !== undefined) return known;
    const pools = [market?.totalPool, ...(market?.outcomePools ?? [])];
    return pools?.some((p) => (p ?? 0n) > 0n) ? unitsOf(...pools) : PAYOUT_DECIMALS;
};

/** Every stake token is a ~$1 stablecoin, so pool size reads directly as dollars. Units are detected unless given. */
export const poolUsd = (raw, decimals) => formatUsd(parseFloat(formatUnits(raw ?? 0n, decimals ?? unitsOf(raw))));
export const toUsd = (raw, decimals = STABLE_DECIMALS) => parseFloat(formatUnits(raw ?? 0n, decimals));

/** Sort comparator: newest market first (poll ids increase with creation). */
export const latestFirst = (a, b) => Number(b?.id) - Number(a?.id);

export const shortAddress = (address) =>
    address ? `${address?.slice(0, 6)}…${address?.slice(-4)}` : "";

/* All times in the UI are shown in Indian Standard Time, on a 24-hour clock. */
const DISPLAY_TZ = "Asia/Kolkata";

export function formatDateTime(date, opts = { dateStyle: "medium", timeStyle: "short" }) {
    return `${new Date(date)?.toLocaleString("en-US", { ...opts, hourCycle: "h23", timeZone: DISPLAY_TZ })} IST`;
}

export function formatDate(date) {
    return `${new Date(date)?.toLocaleDateString("en-US", { timeZone: DISPLAY_TZ })} IST`;
}

/*
 * Imported markets word their deadlines in US Eastern Time ("11:59 PM ET"), so admin screens show that next to IST.
 * The zone name follows daylight saving (EDT / EST), the same switch the source's ET follows.
 */
const SOURCE_TZ = "America/New_York";

export function formatSourceTime(date) {
    return new Date(date)?.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: SOURCE_TZ,
        timeZoneName: "short",
    });
}

/** A readable name for a poll's timezone, for the note under its times. */
const ZONE_NAMES = { "America/New_York": "US Eastern Time (ET)", UTC: "Coordinated Universal Time (UTC)" };
export const timeZoneName = (timeZone) => (timeZone ? ZONE_NAMES[timeZone] ?? timeZone : "India Standard Time (IST)");

/**
 * A poll's time in its own timezone, e.g. "Oct 31, 2026, 11:00 PM EDT". Polls without one (created here) show in IST,
 * exactly as formatDateTime does.
 */
export function formatPollTime(date, timeZone) {
    if (!timeZone || timeZone === DISPLAY_TZ) return formatDateTime(date);
    return new Date(date)?.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone,
        timeZoneName: "short",
    });
}

/** Parse a datetime-local value ("YYYY-MM-DDTHH:mm") entered as IST into epoch ms. */
export function istInputToMs(value) {
    const withSeconds = value?.length === 16 ? `${value}:00` : value;
    return new Date(`${withSeconds}+05:30`)?.getTime();
}

/** Epoch ms → a datetime-local value ("YYYY-MM-DDTHH:mm") in IST — the inverse of istInputToMs. */
export function msToIstInput(ms) {
    if (!Number.isFinite(ms)) return "";
    return new Date(ms + 5.5 * 60 * 60 * 1000)?.toISOString()?.slice(0, 16);
}

/** A readable message for a failed transaction or simulation. */
export function explainError(error) {
    if (!error) return "Transaction failed";
    const text = `${error?.message ?? ""} ${error?.shortMessage ?? ""}`;
    for (const [key, message] of Object.entries(CONTRACT_ERRORS)) {
        if (text?.includes(key)) return message;
    }
    // EIP-1193 4100: the wallet has not approved this site for the connected account (or is locked / on another account)
    for (let e = error, depth = 0; e && depth < 6; e = e?.cause, depth += 1) {
        if (e?.code === 4100 || /has not been authorized by the user/i.test(e?.message ?? "")) {
            return "Your wallet hasn't approved this site for the connected account. Unlock it, select the account you connected with, and connect this site to it (or disconnect and reconnect), then try again.";
        }
    }
    return error?.shortMessage || error?.message || "Transaction failed";
}
